import { Key, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { MdOutlineDelete, MdOutlineEdit, MdOutlineMoreHoriz } from 'react-icons/md';
import { ActionMenu, MenuItemType, TableColumnType } from '@ui';
import { makeCellTextRenderers } from '@features/components/TableCellTextMR';
import { BannedTailNumber } from '@/api/apis/resources';
import { col, COMMON_COLUMN_PRESETS } from '../../utils';

export const BANNED_TAIL_NUMBERS_COLUMNS: TableColumnType[] = [
  col('BannedTailNumber', 'Banned Tail Number', {
    sortable: true,
    minWidthClassName: 'w-80',
  }),
  col('logisticProvider', 'Logistics provider', {
    sortable: false,
    minWidthClassName: 'w-80',
  }),
  col('comment', 'Comment', {
    sortable: false,
    minWidthClassName: 'min-w-96',
  }),
  COMMON_COLUMN_PRESETS.actions,
];

const getCreateActionsMenu = (onEdit: () => void, onDelete: () => void): MenuItemType[] => [
  {
    key: 'edit-banned-tail-number',
    type: 'action',
    to: 'edit-banned-tail-number',
    label: (
      <div className="flex items-center gap-2">
        <MdOutlineEdit size={16} />
        <span className="text-m-r">Edit</span>
      </div>
    ),
    onAction: () => onEdit(),
  },
  {
    key: 'delete-banned-tail-number',
    type: 'action',
    to: 'delete-banned-tail-number',
    label: (
      <div className="flex items-center gap-2 text-danger">
        <MdOutlineDelete size={16} />
        <span className="text-m-r">Delete</span>
      </div>
    ),
    onAction: () => onDelete(),
  },
];

const getEditActionsMenu = (onEdit: () => void): MenuItemType[] => [
  {
    key: 'edit-banned-tail-number',
    type: 'action',
    to: 'edit-banned-tail-number',
    label: (
      <div className="flex items-center gap-2">
        <MdOutlineEdit size={16} />
        <span className="text-m-r">Edit</span>
      </div>
    ),
    onAction: () => onEdit(),
  },
];

export const getRenderBannedTailNumbersTableCell =
  (
    onEdit: (bannedTailNumberId: string) => void,
    onDelete: (bannedTailNumberId: string) => void,
    hasCreatePermissions: boolean,
  ) =>
  (bannedTailNumber: BannedTailNumber, columnKey: Key): ReactNode => {
    const { id, tailNumber, comment, logisticProvider } = bannedTailNumber;
    const onBannedTailNumberEdit = () => onEdit(id);
    const onBannedTailNumberDelete = () => onDelete(id);
    const { text } = makeCellTextRenderers();

    const ACTIONS_MENU_CREATE = getCreateActionsMenu(onBannedTailNumberEdit, onBannedTailNumberDelete);
    const ACTIONS_MENU_EDIT = getEditActionsMenu(onBannedTailNumberEdit);

    switch (columnKey) {
      case 'BannedTailNumber':
        return (
          <Link to={`/admin/configuration/banned_tail_numbers/${id}`}>
            <span className="text-m-r">{tailNumber}</span>
          </Link>
        );
      case 'logisticProvider':
        return text(logisticProvider ? logisticProvider?.name : '-');
      case 'comment':
        return text(comment || '-');
      case 'actions':
        return (
          <ActionMenu
            placement="left-start"
            items={hasCreatePermissions ? ACTIONS_MENU_CREATE : ACTIONS_MENU_EDIT}
            MenuTrigger={
              <div className="">
                <MdOutlineMoreHoriz size={20} />
              </div>
            }
            label="Banned tail numbers actions menu"
          />
        );
      default:
        return null;
    }
  };
